import React, { useState, useEffect, useCallback } from 'react';
import { useWeb3React } from '@web3-react/core';
import { ethers } from 'ethers';
import { callContractWithRetry, getContract, getContractWithSigner, isSupportedNetwork, validateContract } from '../config/contracts';
import { toast } from 'react-toastify';
import { isAddressEqual } from '../utils/walletUtils';

export interface AdminAuthState {
  isOwnerAdmin: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  error: string | null;
  checkAdminStatus: () => Promise<void>;
}

/**
 * Admin Authentication Hook
 * Checks if the currently connected wallet address is the owner of the contract
 */
export const useAdminAuth = (): AdminAuthState => {
  const { account, chainId, provider, isActive } = useWeb3React();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isOwnerAdmin, setIsOwnerAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

   // Get contract instance
    const getContractInstance = useCallback(async () => {
      if (!chainId || !provider || !isSupportedNetwork(chainId)) {
        throw new Error('Network not supported or not connected');
      }
      
      const contract = getContract(chainId, provider);
      
      // Validate the contract (if validation fails, still try to call the contract)
      const isValid = await validateContract(contract.address, provider);
      if (!isValid) {
        console.warn('Contract validation failed, but still attempting to call contract method');
      }
      
      return contract;
    }, [chainId, provider]);
  
    // Get contract instance with signer
    const getSignerContract = useCallback(() => {
      if (!chainId || !provider || !account || !isSupportedNetwork(chainId)) {
        throw new Error('Network not supported or wallet not connected');
      }
      const signer = provider.getSigner(account);
      return getContractWithSigner(chainId, signer);
    }, [chainId, provider, account]);
  
    // Load statistics
    const checkAdminStatus = useCallback(async () => {

         if (!isActive || !account || !chainId || !provider) {
          setIsAdmin(false);
          setError('Please connect your wallet first');
          return;
        }

        if (!isSupportedNetwork(chainId)) {
          setIsAdmin(false);
          setError('Unsupported network, please switch to Polygon mainnet or Amoy testnet');
          return;
        }
         setIsLoading(true);
         setError(null);
       try {
        const contract = await getContractInstance();
        const owner = await callContractWithRetry(async () => {
          return await contract.owner() as string; 
        });

         const adminaccount = await callContractWithRetry(async () => {
          return await contract.getGateKeeper() as string; 
        });

      //    console.info('检查管理员权限owner:', owner);
       //   console.info('检查管理员权限adminaccount:', adminaccount);
           
         const isOwner = isAddressEqual(owner,account);
         const ismanager = isAddressEqual(adminaccount,account);
        //  console.info('检查管理员权限isOwner:', isOwner);
        //  console.info('检查管理员权限ismanager:', ismanager);
            setIsOwnerAdmin(isOwner);
            setIsAdmin(isOwner||ismanager);
         if (!isOwner&&!ismanager) {
            setError('Current account is not a contract administrator and cannot access management functions');
             }
        } catch (err: any) {
          console.error('Failed to check admin permissions:', err);
          let errorMessage = 'Failed to check admin permissions';
          
          if (err.message.includes('Contract not deployed')) {
            errorMessage = 'Contract not deployed to the current network';
          } else if (err.code === 'NETWORK_ERROR') {
            errorMessage = 'Network error, please check your network settings';
          } else if (err.code === 'CALL_EXCEPTION') {
            errorMessage = 'Contract call failed, please check if the contract address is correct';
          }
          
          setError(errorMessage);
          setIsAdmin(false);
          setIsOwnerAdmin(false);
        } finally {
          setIsLoading(false);
        }
    }, [getContractInstance]);

 

  // Re-check admin permissions when wallet connection status, account, or network changes
  useEffect(() => {
    if (isActive && account && chainId) {
      checkAdminStatus();
    } else {
      setIsAdmin(false);
      setIsOwnerAdmin(false);
      setError(null);
    }
  }, [isActive, account, chainId]);

  return {
    isOwnerAdmin,
    isAdmin,
    isLoading,
    error,
    checkAdminStatus
  };
};

/**
 * Admin Permission Guard Component
 */
export const AdminGuard = ({ children, fallback }: {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}): JSX.Element => {
  const { isAdmin, isOwnerAdmin,isLoading, error } = useAdminAuth();
  const { isActive } = useWeb3React();

  if (!isActive) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <h3>Please connect wallet</h3>
        <p>A wallet connection is required to access management functions</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <h3>Verifying permissions...</h3>
        <p>Checking admin permissions, please wait</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: '#f44336' }}>
        <h3>Permission verification failed</h3>
        <p>{error}</p>
      </div>
    );
  }

  if (!isAdmin&&!isOwnerAdmin) {
    return (
      <div>
        {fallback || (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <h3>Access Denied</h3>
            <p>Only contract administrators can access this feature</p>
          </div>
        )}
      </div>
    );
  }

  return <div>{children}</div>;
};